// Import required Firebase modules
// @ts-nocheck
import { initializeApp } from "firebase/app";
import { getAnalytics, isSupported as isAnalyticsSupported } from "firebase/analytics";
import { setAnalyticsInstance } from "./utils/analyticsInstance";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithRedirect,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  onAuthStateChanged,
  connectAuthEmulator,
  getRedirectResult,
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  connectFirestoreEmulator
} from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions"; // Import Functions modules
import { getStorage, connectStorageEmulator } from "firebase/storage"; // Import Storage modules
import { whenAnalyticsConsentGranted } from "./utils/analyticsConsent";

// Firebase configuration object
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
// Firebase Analytics egresses usage data to Google (US), so it must not start
// until the visitor has granted analytics consent (SEC-6). It is also browser-
// only and throws when measurementId is absent (see .env.example) or the
// environment is unsupported (e.g. jsdom under Jest) — so it is initialized
// defensively, and only once consent is (or becomes) granted.
if (firebaseConfig.measurementId) {
  whenAnalyticsConsentGranted(() => {
    isAnalyticsSupported()
      .then((supported) => {
        if (supported) setAnalyticsInstance(getAnalytics(app));
      })
      .catch(() => {});
  });
}
const storage = getStorage(app);
// Initialize Firebase services
const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();
const db = getFirestore(app);
// Explicitly bind Functions to the deployed region to avoid routing mismatches
const functions = getFunctions(app, 'europe-west2'); // Initialize Functions

/**
 * Local development normally talks to the emulator suite. Setting
 * REACT_APP_USE_EMULATORS=false in .env.local opts out and points `npm start`
 * at whatever project the REACT_APP_FIREBASE_* values name — which is the only
 * way to see real member screens on this machine, because the Firestore and
 * Storage emulators need Java and it is not installed here.
 *
 * Fail-safe by design: anything other than the literal string 'false' keeps the
 * emulators, so an unset or misspelt flag cannot silently point a dev build at
 * a real project. Production builds never reach this branch at all —
 * NODE_ENV is 'production' there.
 *
 * If you do opt out, you are pointing a local build at a project holding real
 * member data. Use staging, never production.
 */
const USE_EMULATORS = process.env.REACT_APP_USE_EMULATORS !== 'false';

// Connect to emulators if running locally
if (process.env.NODE_ENV === 'development' && USE_EMULATORS) {
  connectAuthEmulator(auth, 'http://localhost:9099');
  connectFirestoreEmulator(db, 'localhost', 8080); // Firestore emulator
  connectFunctionsEmulator(functions, 'localhost', 5001); // Functions emulator
  connectStorageEmulator(storage, 'localhost', 9199);
}

// Export all Firebase services needed in the app
export {
  auth,
  db,
  functions, // Export Functions
  storage,
  doc,
  getDoc,
  signOut,
  googleProvider,
  signInWithRedirect,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  onAuthStateChanged,
  getRedirectResult,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail
};